/**
 * ðŸ“‹ BlueprintLibraryTable â€” Excelência Module
 *
 * Tabela real da Biblioteca de Blueprints com colunas:
 *   Nome | Versão | Status | Em Uso | Criado Por | Atualizado | Ações
 *
 * Status badges:
 *   draft → amarelo | published → azul | standard → roxo | archived → cinza
 *
 * Menu contextual (â‹®):
 *   Editar Â· Versionar Â· Publicar Â· Definir como padrão
 *   Arquivar Â· Ver uso Â· Excluir (somente se não em uso)
 *
 * Memoizada para performance.
 */

import React, { useCallback, useState, useMemo } from 'react';
import {
  Layers,
  Trash2,
  Loader2,
  Award,
  GitBranch,
  Send,
  MoreVertical,
  Search,
  Filter,
  Archive,
  FileEdit,
  BarChart3,
  RotateCcw,
  User,
  ScrollText,
} from 'lucide-react';
import type { ProcessBlueprint, BlueprintStatus, BlueprintUsageCount } from '../../../types/processBlueprint';

// ============================================================================
// TIPOS
// ============================================================================

interface BlueprintLibraryTableProps {
  blueprints: ProcessBlueprint[];
  processingId: string | null;
  usageCounts?: BlueprintUsageCount[];
  /** Se true, exibe ações de governança (Publicar, Padrão, Arquivar, Excluir) */
  canGovernance?: boolean;
  onEdit?: (blueprint: ProcessBlueprint) => void;
  onVersion: (blueprint: ProcessBlueprint) => void;
  onPublish: (blueprint: ProcessBlueprint) => void;
  onToggleStandard: (blueprint: ProcessBlueprint) => void;
  onArchive: (blueprint: ProcessBlueprint) => void;
  onReactivate?: (blueprint: ProcessBlueprint) => void;
  onViewUsage: (blueprint: ProcessBlueprint) => void;
  onViewLogs?: (blueprint: ProcessBlueprint) => void;
  onDelete: (blueprint: ProcessBlueprint) => void;
}

type SortKey = 'name' | 'status' | 'semver' | 'usage' | 'createdBy' | 'updatedAt';
type SortDir = 'asc' | 'desc';
type FilterStatus = 'all' | BlueprintStatus;

// ============================================================================
// HELPERS
// ============================================================================

const STATUS_BADGE: Record<BlueprintStatus, { label: string; className: string }> = {
  draft: {
    label: 'Rascunho',
    className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  },
  published: {
    label: 'Publicado',
    className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  },
  standard: {
    label: 'Padrão',
    className: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  },
  archived: {
    label: 'Arquivado',
    className: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-500',
  },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

function formatCreator(bp: ProcessBlueprint): string {
  if (bp.createdByName) return bp.createdByName;
  if (!bp.createdBy) return 'â€”';
  return bp.createdBy.length > 8 ? bp.createdBy.slice(0, 8) + 'â€¦' : bp.createdBy;
}

function compareSemver(a: ProcessBlueprint, b: ProcessBlueprint): number {
  const aMajor = a.versionMajor ?? 1;
  const bMajor = b.versionMajor ?? 1;
  if (aMajor !== bMajor) return aMajor - bMajor;

  const aMinor = a.versionMinor ?? 0;
  const bMinor = b.versionMinor ?? 0;
  if (aMinor !== bMinor) return aMinor - bMinor;

  const aPatch = a.versionPatch ?? 0;
  const bPatch = b.versionPatch ?? 0;
  return aPatch - bPatch;
}

// ============================================================================
// ROW ACTIONS DROPDOWN (â‹®)
// ============================================================================

const RowActions: React.FC<{
  blueprint: ProcessBlueprint;
  usageCount: number;
  isProcessing: boolean;
  canGovernance?: boolean;
  onEdit?: () => void;
  onVersion: () => void;
  onPublish: () => void;
  onToggleStandard: () => void;
  onArchive: () => void;
  onReactivate?: () => void;
  onViewUsage: () => void;
  onViewLogs?: () => void;
  onDelete: () => void;
}> = React.memo(
  ({
    blueprint,
    usageCount,
    isProcessing,
    canGovernance = false,
    onEdit,
    onVersion,
    onPublish,
    onToggleStandard,
    onArchive,
    onReactivate,
    onViewUsage,
    onViewLogs,
    onDelete,
  }) => {
    const [open, setOpen] = useState(false);

    const toggle = useCallback(() => setOpen(prev => !prev), []);
    const close = useCallback(() => setOpen(false), []);

    const actionItem = useCallback(
      (label: string, icon: React.ReactNode, handler: () => void, danger = false) => (
        <button
          key={label}
          disabled={isProcessing}
          onClick={() => {
            handler();
            close();
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors disabled:opacity-40 ${
            danger
              ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
          }`}
        >
          {icon}
          {label}
        </button>
      ),
      [isProcessing, close],
    );

    return (
      <div className="relative flex justify-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          {isProcessing ? (
            <Loader2 size={14} className="animate-spin text-purple-500" />
          ) : (
            <MoreVertical size={14} className="text-gray-500 dark:text-slate-400" />
          )}
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={close} />
            <div className="absolute right-0 top-8 z-50 w-52 bg-white dark:bg-slate-900 rounded border border-gray-200 dark:border-slate-700 shadow-xl py-1">
              {/* â”€â”€ Editar â”€â”€ */}
              {/* Rascunho: edição direta permitida */}
              {onEdit && blueprint.status === 'draft' &&
                actionItem('Editar', <FileEdit size={12} />, onEdit)}
              {/* Publicado/Padrão: edição bloqueada â€” exige nova versão */}
              {onEdit && (blueprint.status === 'published' || blueprint.status === 'standard') &&
                actionItem('Editar (Nova Versão)', <GitBranch size={12} />, onEdit)}

              {/* â”€â”€ Versionar â”€â”€ */}
              {blueprint.status !== 'archived' &&
                actionItem('Versionar', <GitBranch size={12} />, onVersion)}

              {/* â”€â”€ Publicar (somente rascunho) â”€â”€ */}
              {canGovernance && blueprint.status === 'draft' &&
                actionItem('Publicar', <Send size={12} />, onPublish)}

              {/* â”€â”€ Arquivar (somente publicado) â”€â”€ */}
              {canGovernance && blueprint.status === 'published' &&
                actionItem('Arquivar', <Archive size={12} />, onArchive)}

              {/* â”€â”€ Reativar (somente arquivado) â”€â”€ */}
              {blueprint.status === 'archived' && onReactivate &&
                actionItem('Reativar', <RotateCcw size={12} />, onReactivate)}

              {/* â”€â”€ Definir como padrão / Remover padrão (governança) â”€â”€ */}
              {canGovernance && blueprint.status === 'published' &&
                actionItem('Definir como Padrão', <Award size={12} />, onToggleStandard)}
              {canGovernance && blueprint.status === 'standard' &&
                actionItem('Remover Padrão', <Award size={12} />, onToggleStandard)}

              <div className="border-t border-gray-100 dark:border-slate-800 my-1" />

              {/* â”€â”€ Ver uso â”€â”€ */}
              {actionItem('Ver Uso', <BarChart3 size={12} />, onViewUsage)}

              {/* â”€â”€ Ver histórico â”€â”€ */}
              {onViewLogs &&
                actionItem('Ver Histórico', <ScrollText size={12} />, onViewLogs)}

              {/* â”€â”€ Excluir (governança, somente se não em uso) â”€â”€ */}
              {canGovernance && usageCount === 0 && (
                <>
                  <div className="border-t border-gray-100 dark:border-slate-800 my-1" />
                  {actionItem('Excluir', <Trash2 size={12} />, onDelete, true)}
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  },
);

RowActions.displayName = 'RowActions';

// ============================================================================
// MAIN TABLE
// ============================================================================

export const BlueprintLibraryTable: React.FC<BlueprintLibraryTableProps> = React.memo(
  ({
    blueprints,
    processingId,
    usageCounts,
    canGovernance = false,
    onEdit,
    onVersion,
    onPublish,
    onToggleStandard,
    onArchive,
    onReactivate,
    onViewUsage,
    onViewLogs,
    onDelete,
  }) => {
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    // Build usage count map for quick lookup
    const usageMap = useMemo(() => {
      const lookup: Record<string, number> = {};
      if (usageCounts) {
        usageCounts.forEach(u => { lookup[u.blueprintId] = u.usageCount; });
      }
      return lookup;
    }, [usageCounts]);

    // Toggle sort
    const handleSort = useCallback(
      (key: SortKey) => {
        if (sortKey === key) {
          setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
          setSortKey(key);
          setSortDir('asc');
        }
      },
      [sortKey],
    );

    // Filter + sort
    const rows = useMemo(() => {
      let result = [...blueprints];

      // Search
      if (search.trim()) {
        const q = search.toLowerCase();
        result = result.filter(
          b =>
            b.name.toLowerCase().includes(q) ||
            (b.description ?? '').toLowerCase().includes(q),
        );
      }

      // Status filter
      if (filterStatus !== 'all') {
        result = result.filter(b => b.status === filterStatus);
      }

      // Sort
      result.sort((a, b) => {
        let cmp = 0;
        switch (sortKey) {
          case 'name':
            cmp = a.name.localeCompare(b.name, 'pt-BR');
            break;
          case 'status':
            cmp = a.status.localeCompare(b.status);
            break;
          case 'semver':
            cmp = compareSemver(a, b);
            break;
          case 'usage':
            cmp = (usageMap[a.id] ?? 0) - (usageMap[b.id] ?? 0);
            break;
          case 'createdBy':
            cmp = (a.createdByName ?? a.createdBy ?? '').localeCompare(b.createdByName ?? b.createdBy ?? '');
            break;
          case 'updatedAt':
            cmp = a.updatedAt.localeCompare(b.updatedAt);
            break;
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });

      return result;
    }, [blueprints, search, filterStatus, sortKey, sortDir, usageMap]);

    const SortHeader: React.FC<{ label: string; colKey: SortKey }> = useCallback(
      ({ label, colKey }) => (
        <th
          onClick={() => handleSort(colKey)}
          className="px-3 py-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.14em] cursor-pointer select-none whitespace-nowrap"
        >
          <span className="flex items-center gap-1">
            {label}
            {sortKey === colKey && (
              <span className="enterprise-text-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>
            )}
          </span>
        </th>
      ),
      [handleSort, sortKey, sortDir],
    );

    // Empty state
    if (blueprints.length === 0) {
      return (
        <div className="enterprise-card-surface flex min-h-[200px] flex-col items-center justify-center gap-1 px-6 text-center enterprise-text-secondary">
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] enterprise-text-muted">Nenhum modelo encontrado</p>
          <p className="text-xs enterprise-text-secondary">
            Modelos são criados automaticamente ao integrar Mapas e Workflows.
            Use as ações nos respectivos módulos para começar.
          </p>
        </div>
      );
    }

    return (
      <div>
        {/* Toolbar: Search + Filters */}
        <div className="flex items-center gap-3 px-2 py-1 border-b border-zinc-800/50">
          <div className="relative flex-1 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 enterprise-text-muted"
            />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar modelo..."
              className="w-full pl-9 pr-3 py-2 text-xs enterprise-surface-1 border border-[var(--border-strong)] rounded focus:outline-none enterprise-text-primary placeholder:enterprise-text-muted font-medium"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Filter size={13} className="enterprise-text-muted" />
            {(['all', 'draft', 'published', 'standard', 'archived'] as const).map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`text-xs px-2.5 py-1 rounded-sm transition-colors ${
                  filterStatus === s
                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium'
                    : 'enterprise-text-muted hover:enterprise-surface-3'
                }`}
              >
                {s === 'all'
                  ? 'Todos'
                  : s === 'draft'
                  ? 'Rascunho'
                  : s === 'published'
                  ? 'Publicado'
                  : s === 'standard'
                  ? 'Padrão'
                  : 'Arquivado'}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="enterprise-table-shell overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed text-[11px]">
              <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
                <tr>
                  <SortHeader label="Nome" colKey="name" />
                  <SortHeader label="Versão" colKey="semver" />
                  <SortHeader label="Status" colKey="status" />
                  <SortHeader label="Em Uso" colKey="usage" />
                  <SortHeader label="Criado Por" colKey="createdBy" />
                  <SortHeader label="Atualizado" colKey="updatedAt" />
                  <th className="px-3 py-2 text-center font-mono text-[10px] font-medium uppercase tracking-[0.14em] w-16">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(bp => {
                  const badge = STATUS_BADGE[bp.status];
                  const isBeingProcessed = processingId === bp.id;
                  const usage = usageMap[bp.id] ?? 0;

                  return (
                    <tr
                      key={bp.id}
                      className={`enterprise-table-row ${
                        isBeingProcessed
                          ? 'opacity-60 pointer-events-none'
                          : ''
                      }`}
                    >
                      {/* Nome */}
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-2 min-w-0">
                          {bp.isCorporateStandard && (
                            <Award size={13} className="text-amber-500 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold enterprise-text-primary truncate max-w-[280px]">
                              {bp.name}
                            </p>
                            {bp.description && (
                              <p className="text-[10px] enterprise-text-muted truncate max-w-[280px]">
                                {bp.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Versão */}
                      <td className="px-3 py-2 align-middle">
                        <span className="font-mono text-[10px] uppercase tracking-[0.12em] enterprise-text-primary">
                          v{bp.versionSemver}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2 align-middle">
                        <span
                          className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-semibold leading-tight whitespace-nowrap ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>

                      {/* Em Uso */}
                      <td className="px-3 py-2 align-middle">
                        {usage > 0 ? (
                          <span className="font-mono text-[11px] font-medium text-blue-600 dark:text-blue-400">
                            {usage} projeto{usage !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-[11px] enterprise-text-muted">—</span>
                        )}
                      </td>

                      {/* Criado Por */}
                      <td className="px-3 py-2 align-middle">
                        <span className="flex items-center gap-1.5 text-[11px] enterprise-text-secondary">
                          <User size={11} className="enterprise-text-muted flex-shrink-0" />
                          <span className="truncate max-w-[120px]" title={bp.createdByName || bp.createdBy || ''}>
                            {formatCreator(bp)}
                          </span>
                        </span>
                      </td>

                      {/* Atualizado */}
                      <td className="px-3 py-2 align-middle font-mono text-[11px] enterprise-text-muted whitespace-nowrap">
                        {formatDate(bp.updatedAt)}
                      </td>

                      {/* Ações */}
                      <td className="px-3 py-2 align-middle">
                        <RowActions
                          blueprint={bp}
                          usageCount={usage}
                          isProcessing={isBeingProcessed}
                          canGovernance={canGovernance}
                          onEdit={onEdit ? () => onEdit(bp) : undefined}
                          onVersion={() => onVersion(bp)}
                          onPublish={() => onPublish(bp)}
                          onToggleStandard={() => onToggleStandard(bp)}
                          onArchive={() => onArchive(bp)}
                          onReactivate={onReactivate ? () => onReactivate(bp) : undefined}
                          onViewUsage={() => onViewUsage(bp)}
                          onViewLogs={onViewLogs ? () => onViewLogs(bp) : undefined}
                          onDelete={() => onDelete(bp)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Footer */}
          <div className="border-t border-[var(--border-strong)] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] enterprise-text-muted">
            {rows.length} de {blueprints.length} modelo{blueprints.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    );
  },
);

BlueprintLibraryTable.displayName = 'BlueprintLibraryTable';
